<template>
    <section class="valuation-layout  container" id="vehicle-condition">
        <h1 class="valuation-layout__header">Free Instant Vehicle Valuation</h1>
        <StepBar />
        <StepLink :step="step - 1" />

        <div class="valuation-layout__container">
            <valuation-amount v-if="valuation" :show-cta="false"
                valuation-title="Your Estimated Vehicle Valuation"
                valuation-text="Your estimate will update after each step, including after we’ve verified your vehicle’s history through a HPI check.">
            </valuation-amount>

            <vehicle-image />

            <div class="valuation-layout__main-section">
                <h3 class="valuation-layout__title">What is the condition of the vehicle</h3>
                <p>This will give us a better understanding of the overall health and condition of your vehicle.</p>

                <div class="rd-card rd-card--border-top valuation-layout__card">
                    <div class="valuation-layout__condition-container">
                        <h3 class="card__heading">Please select one of the below:</h3>
                        <form @submit.prevent="submit" ref="form" data-form-name="Valuation - Vehicle Condition">
                            <RadioOptionButtons
                                :options="conditionOptions"
                                v-model="vehicleConditionFromCustomer"
                                name="vehicle_condition"
                                validate="radio"
                                validate-message="Please select an option."
                                error-target="condition-error"
                            />
                        </form>
                        <template v-if="vehicleConditionFromCustomer">
                            <h3 class="card__heading">You selected: <span v-if="vehicleConditionFromCustomer">{{ selectedConditionLabel }} </span>
                            </h3>
                            <p v-if="vehicleConditionFromCustomer === 'Excellent'">Your vehicle is used but is still in excellent condition and looks like new. Your vehicle is in excellent mechanical condition with the body and interior free of wear or visible defects.</p>
                            <p v-if="vehicleConditionFromCustomer  === 'Good'">Your vehicle is in good condition with standard wear and tear to be expected for the age and mileage of your vehicle. There are no major issues with the vehicle, no bodywork damage or warning lights.</p>
                            <p v-if="vehicleConditionFromCustomer === 'Signs of Wear'">Your vehicle has some repairable bodywork defects but is free of any major mechanical problems.</p>
                            <p v-if="vehicleConditionFromCustomer  === 'Visible Damage'">Your vehicle has some mechanical and bodywork defects which require repairing or replacing.</p>
                        </template>
                    </div>
                    <div class="valuation-layout__button-area">
                        <loading-spinner v-if="submitting" />
                        <template v-else>
                            <button type="button" class="btn btn--primary card__button valuation-layout__button" @click.prevent="submit">
                                <span>Continue</span>
                            </button>
                            <a v-if="authenticated" href="/my-account/valuations" target="_blank" class="link  link--primary  link--has-icon valuation-layout__button">
                                Save Progress To My Account
                            </a>
                            <a v-else href="#" class="link link--primary  link--has-icon valuation-layout__button" @click.prevent="registerAndSave">
                                <span>Save Progress To My Account</span>
                            </a>
                        </template>
                    </div>
                </div>
            </div>
            <div class="valuation-layout__vehicle-details">
                <VehicleDetails :displayImage="false"/>
                <button @click="newValuation()" class="btn  btn--full-width btn--primary-inverted">Start new valuation</button>
            </div>
        </div>
        <modal :open="showEnquiryMaxWarning" :vertical-content="true" :is-card="true" :show-close-icon="true" @close="showEnquiryMaxWarning = false">
            <template #header>
                <h2 class="modal__title">Valuation Not Linked To Enquiry Max</h2>
            </template>
            <template #body>
                <p>
                    This valuation is not linked to Enquiry Max and you will therefore not see the valuation. Please check the below and retry. If you are confident you are following all these points, please contact
                    <a href="mailto:jason.baber@stellantis.com">jason.baber@stellantis.com</a> or
                    <a href="mailto:conor.power@stellantis.com">conor.power@stellantis.com</a>.
                </p>

                <ul>
                    <li>Have you submitted the appraisal in Enquiry Max before carrying this appraisal out on the website?</li>
                    <li>Are you been logged into your dealer account on the website?</li>
                    <li>Is the mileage of the vehicle when valuing it on the website close to or matching what was added in enquiry max?</li>
                </ul>
            </template>
        </modal>
    </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { StepBar, StepLink, ValuationAmount, VehicleDetails, RadioOptionButtons, LoadingSpinner, VehicleImage } from "../";
import Modal from "../../../core/components/Modal.vue";

export default {
    components: {
        VehicleDetails,
        StepLink,
        StepBar,
        ValuationAmount,
        RadioOptionButtons,
        LoadingSpinner,
        VehicleImage,
        Modal
    },
    data() {
        return {
            submitting: false,
            showEnquiryMaxWarning: false,
            validator: false,
            conditionOptions:[
                { label: 'Excellent', value: 'Excellent' },
                { label: 'Good', value: 'Good' },
                { label: 'Signs of Wear', value: 'Signs of Wear' },
                { label: 'Visible Damage', value: 'Visible Damage' }
            ]
        };
    },

    methods: {
        ...mapMutations("ValuationTool", ["incrementStep" , "goToPosition"]),
        ...mapMutations("ValuationExtended", ["setCondition"]),
        ...mapActions("ValuationExtended", ["registerAndSave", "saveAndContinue"]),
        ...mapActions("ValuationTool", ["newValuation"]),

        goToPosition(position) {
            const targetElement = document.getElementById(position);
            if (targetElement) {
                const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - 60;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },
        submit() {
            if (this.submitting) {
                console.warn("Form is already submitting");
                return;
            }
            this.submitting = true;

            this.validator.validateAndSubmit(null, false, formData => {
                return new Promise((resolve, reject) => {
                    this.saveAndContinue()
                        .then(() => {
                            this.submitting = false;
                            resolve();
                        })
                        .catch(error => {
                            this.submitting = false;
                            reject(error);
                        });
                });
            })
            .catch(error => {
                this.submitting = false;
            });
        }
    },

    computed: {
        ...mapState('ValuationTool', ['valuation', 'step', 'enquiryMaxWarning', 'isDealer']),
        ...mapState('ValuationExtended', ['serviceHistory']),
        ...mapState('Auth', ['authenticated']),
        ...mapGetters('ValuationTool', ['vehicleImage']),

        vehicleConditionFromCustomer: {
            get() {
                return this.$store.state.ValuationExtended.serviceData.VehicleConditionFromCustomer;
            },
            set(value) {
                this.setCondition(value);
            }
        },

        selectedConditionLabel() {
            const selectedOption = this.conditionOptions.find(
                option => option.value === this.vehicleConditionFromCustomer
            );
            return selectedOption ? selectedOption.label : '';
        },

        formattedAmount() {
            return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP',  minimumFractionDigits: 0,
            maximumFractionDigits: 0 }).format(this.valuation.valuation);
        }
    },
    mounted() {
        if (this.enquiryMaxWarning == true && this.isDealer == true) {
            this.showEnquiryMaxWarning = true;
        }
        this.validator = new ContactFormValidator({
            element: this.$refs['form'],
            vueForm: true,
            redesign: true,
        });
        this.goToPosition('valuation-start')
    }
}
</script>